import React, { useState } from 'react';
import { Box, TextField } from '@mui/material';
import { FONTANA_SLIDE_COLORS } from '../types';
import { PRES, TYPE } from '../chrome';
import { isSameColor, normalizeHex } from '../colorField';
import { COPY } from '../presentationCopy';
import { focusVisibleSx } from '../ui/focus';
import PanelRow from '../ui/PanelRow';
import { usePresentationSettings } from '../PresentationSettingsContext';
import { visibleSettingsColors } from '../presentationSettings';

interface ColorFieldProps {
  label: string;
  value?: string;
  allowNone?: boolean;
  disabled?: boolean;
  onChange: (value: string) => void;
  flush?: boolean;
  grouped?: boolean;
}

const ColorField: React.FC<ColorFieldProps> = ({
  label, value, allowNone, disabled, onChange, flush, grouped = false,
}) => {
  const { settings } = usePresentationSettings();
  const liveColors = visibleSettingsColors(settings);
  const paletteColors: Array<{ hex: string; name: string; group: 'primary' | 'complement' | 'accent' }> = liveColors.length
    ? liveColors
    : FONTANA_SLIDE_COLORS.map((color) => ({ hex: color.hex, name: color.name, group: color.group }));
  const current = value && value !== 'transparent' ? normalizeHex(value) : '';
  const [hexOpen, setHexOpen] = useState(Boolean(current && !paletteColors.some((color) => isSameColor(color.hex, current))));
  const groups = [
    { key: 'primary', title: 'Primär' },
    { key: 'complement', title: 'Komplement' },
    { key: 'accent', title: 'Accent' },
  ] as const;

  const swatch = (hex: string, name: string, none = false) => {
    const selected = none ? !current : isSameColor(current, hex);
    return (
      <Box
        key={hex + name}
        component="button"
        type="button"
        title={name}
        disabled={disabled}
        onClick={() => onChange(none ? 'transparent' : hex)}
        sx={{
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: none ? PRES.surface : hex,
          backgroundImage: none
            ? 'linear-gradient(135deg, transparent 46%, #c0392b 46%, #c0392b 54%, transparent 54%)'
            : undefined,
          border: selected ? `2px solid ${PRES.interactive}` : `1px solid ${hex === '#FFFFFF' || none ? PRES.borderStrong : 'rgba(0,0,0,0.12)'}`,
          boxShadow: selected ? `0 0 0 1px ${PRES.surface}` : 'none',
          cursor: disabled ? 'not-allowed' : 'pointer',
          p: 0,
          ...focusVisibleSx,
        }}
      />
    );
  };

  const palette = (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
        {allowNone && swatch('none', COPY.noFill, true)}
        {!grouped && paletteColors.map((color) => swatch(color.hex, color.name))}
      </Box>
      {grouped && groups.map((group) => (
        <Box key={group.key} sx={{ mt: 0.75 }}>
          <Box sx={{ fontFamily: TYPE.ui, ...TYPE.caption, color: PRES.textTertiary, mb: 0.4 }}>{group.title}</Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
            {paletteColors.filter((color) => color.group === group.key).map((color) => swatch(color.hex, color.name))}
          </Box>
        </Box>
      ))}
      {settings.allowCustomHex && (
        <>
          <Box
            component="button"
            type="button"
            onClick={() => setHexOpen((open) => !open)}
            sx={{
              mt: 0.75,
              border: 0,
              bgcolor: 'transparent',
              color: PRES.textSecondary,
              fontFamily: TYPE.ui,
              ...TYPE.caption,
              cursor: 'pointer',
              p: 0,
              ...focusVisibleSx,
            }}
          >
            {COPY.customColor}
          </Box>
          {hexOpen && (
            <TextField
              size="small"
              value={current || '#'}
              disabled={disabled}
              onChange={(event) => {
                const next = normalizeHex(event.target.value);
                if (next) onChange(next);
              }}
              sx={{ mt: 0.75, width: 112, '& input': { fontFamily: TYPE.tabular, fontSize: 12 } }}
            />
          )}
        </>
      )}
    </>
  );

  if (flush) return <Box>{palette}</Box>;
  return <PanelRow label={label} stacked>{palette}</PanelRow>;
};

export default ColorField;
